import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  Layout,
  Typography,
  Button,
  Card,
  Tag,
  Space,
  Tabs,
  Avatar,
  Badge,
  Descriptions,
  Upload,
  List,
  Input,
  Select,
  Timeline,
  Tooltip,
  Divider,
  Row,
  Col,
  Dropdown,
  message,
  Modal,
  Popconfirm,
  Progress,
  theme,
  Flex,
  Statistic,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  PauseCircleOutlined,
  FileOutlined,
  DownloadOutlined,
  UploadOutlined,
  SendOutlined,
  PaperClipOutlined,
  UserOutlined,
  TeamOutlined,
  HistoryOutlined,
  CarOutlined,
  SettingOutlined,
  CopyOutlined,
  MoreOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  FileDoneOutlined,
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
  MessageOutlined,
  CreditCardOutlined,
  ThunderboltOutlined,
  SafetyCertificateOutlined,
  CloudUploadOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import { getChartSeriesColors, hexToRgba } from '../../shared/styles';

const { Title, Text, Paragraph } = Typography;
const { Content, Sider } = Layout;
const { TextArea } = Input;
const { Dragger } = Upload;

const TunerRequestDetailAntD = () => {
  const [activeTab, setActiveTab] = useState('details');
  const [chatMessage, setChatMessage] = useState('');
  const [selectedAssignee, setSelectedAssignee] = useState(1);
  const [status, setStatus] = useState('pending');
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const chatEndRef = useRef(null);
  const { token } = theme.useToken();
  const chartColors = useMemo(() => getChartSeriesColors(token), [token]);
  const selectedTint = useMemo(() => hexToRgba(token.colorPrimary, 0.1), [token]);
  const tunerMessageBg = useMemo(
    () => `linear-gradient(135deg, ${hexToRgba(token.colorPrimary, 0.2)} 0%, ${hexToRgba(token.colorPrimary, 0.1)} 100%)`,
    [token],
  );

  // Mock data
  const request = {
    id: 'REQ-2025-0743',
    createdAt: '29 juil. 2025 12:43',
    status: 'pending',
    priority: 'normal',
    client: {
      name: 'AutoSpeed35',
      email: 'autospeed35@gmail.com',
      credits: 140,
      avatar: 'AS',
    },
    vehicle: {
      brand: 'RENAULT',
      model: 'SCENIC',
      power: '115 CV',
      gearbox: 'MANUELLE 5',
      engine: '1.6L',
      year: '2003',
      plate: '301AXN35',
      vin: 'VF1JM0C0H28979379',
    },
    ecu: {
      brand: 'Sagem',
      model: 'S3000',
      readingTool: 'Dimsport New Genius',
      hwNumber: '8200298457',
      swNumber: '8200198107',
    },
    tuning: {
      products: ['Option DTC', 'Suppression actionneur', 'Hardcut', 'Rev limiter', 'Speed limit'],
      base: 'Stage 1',
      comments: 'DTC P0340 - Le client souhaite garder le limiteur à 180 km/h',
      credits: 25,
    },
    files: {
      received: [
        { id: 1, name: '301AXN35.BIN', size: '2.4 MB', date: '29/07/2025 12:43', type: 'original' },
      ],
      sent: [
        { id: 2, name: 'Renault_Scenic_110PS_(P0340)_Stage1.bin', size: '2.4 MB', date: '29/07/2025 12:48', type: 'modified', status: 'delivered' },
      ],
    },
  };

  const teamMembers = useMemo(() => {
    const p = (i: number) => chartColors[i % chartColors.length];
    return [
      { id: 1, name: 'Thomas M.', role: 'Senior Tuner', avatar: 'TM', color: p(0), available: true, workload: 8 },
      { id: 2, name: 'Julie R.', role: 'Tuner', avatar: 'JR', color: p(1), available: true, workload: 12 },
      { id: 3, name: 'Marc D.', role: 'Tuner', avatar: 'MD', color: p(2), available: false, workload: 15 },
      { id: 4, name: 'Sophie L.', role: 'Junior Tuner', avatar: 'SL', color: p(3), available: true, workload: 5 },
    ];
  }, [chartColors]);

  const chatMessages = [
    { id: 1, sender: 'client', name: 'AutoSpeed35', message: 'Bonjour, le client souhaite garder le limiteur de vitesse à 180 km/h', time: '12:45', date: '29 juil.' },
    { id: 2, sender: 'tuner', name: 'Thomas M.', message: 'Bien noté, je fais les modifications. Le DTC P0340 sera également traité.', time: '12:52', date: '29 juil.' },
    { id: 3, sender: 'tuner', name: 'Thomas M.', message: 'Fichier prêt et envoyé. Bonne réception !', time: '12:58', date: '29 juil.' },
    { id: 4, sender: 'system', name: 'Système', message: 'Fichier Renault_Scenic_110PS_(P0340)_Stage1.bin envoyé', time: '12:58', date: '29 juil.' },
  ];

  const activityLog = [
    { type: 'completed', label: 'Demande terminée', time: '12:58', date: '29 juil. 2025', user: 'Thomas M.', color: 'green' },
    { type: 'file', label: 'Fichier modifié envoyé', time: '12:58', date: '29 juil. 2025', user: 'Thomas M.', color: 'blue' },
    { type: 'message', label: 'Message envoyé', time: '12:52', date: '29 juil. 2025', user: 'Thomas M.', color: 'purple' },
    { type: 'assigned', label: 'Assigné à Thomas M.', time: '12:45', date: '29 juil. 2025', user: 'System', color: 'purple' },
    { type: 'started', label: 'Traitement démarré', time: '12:43', date: '29 juil. 2025', user: 'System', color: 'orange' },
    { type: 'created', label: 'Demande créée', time: '12:43', date: '29 juil. 2025', user: 'AutoSpeed35', color: 'gray' },
  ];

  const statusOptions = [
    { value: 'pending', label: 'En attente', color: 'warning', icon: <ClockCircleOutlined /> },
    { value: 'processing', label: 'En cours', color: 'processing', icon: <SyncOutlined spin /> },
    { value: 'review', label: 'En révision', color: 'purple', icon: <EyeOutlined /> },
    { value: 'completed', label: 'Terminée', color: 'success', icon: <CheckCircleOutlined /> },
    { value: 'onhold', label: 'En pause', color: 'default', icon: <PauseCircleOutlined /> },
  ];

  const currentStatus = statusOptions.find(s => s.value === status);
  const currentAssignee = teamMembers.find(m => m.id === selectedAssignee);

  // Scroll to bottom of chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Handle file upload
  const uploadProps = {
    name: 'file',
    multiple: true,
    action: 'https://api.example.com/upload',
    onChange(info) {
      const { status } = info.file;
      if (status === 'done') {
        message.success(`${info.file.name} envoyé avec succès.`);
      } else if (status === 'error') {
        message.error(`Erreur lors de l'envoi de ${info.file.name}.`);
      }
    },
  };

  // Handle send message
  const handleSendMessage = () => {
    if (chatMessage.trim()) {
      message.success('Message envoyé');
      setChatMessage('');
    }
  };

  const getActivityIcon = useCallback(
    (type: string) => {
      const icons: Record<string, React.ReactNode> = {
        completed: <CheckCircleOutlined style={{ color: token.colorSuccess }} />,
        file: <FileOutlined style={{ color: token.colorInfo }} />,
        message: <MessageOutlined style={{ color: token.colorPrimary }} />,
        assigned: <UserOutlined style={{ color: token.colorPrimary }} />,
        started: <ClockCircleOutlined style={{ color: token.colorWarning }} />,
        created: <PlusOutlined style={{ color: token.colorTextTertiary }} />,
      };
      return icons[type] || <ClockCircleOutlined />;
    },
    [token],
  );

  // Details Tab Content
  const DetailsContent = () => (
    <Row gutter={[20, 20]}>
      {/* Vehicle Info */}
      <Col span={12}>
        <Card
          title={
            <Space>
              <CarOutlined style={{ color: token.colorInfo }} />
              <span>Véhicule</span>
            </Space>
          }
          size="small"
          styles={{ body: { padding: '16px 20px' } }}
        >
          <Descriptions column={2} size="small" colon={false}>
            <Descriptions.Item label={<Text type="secondary">Marque</Text>}>
              <Text strong>{request.vehicle.brand}</Text>
            </Descriptions.Item>
            <Descriptions.Item label={<Text type="secondary">Modèle</Text>}>
              <Text strong>{request.vehicle.model}</Text>
            </Descriptions.Item>
            <Descriptions.Item label={<Text type="secondary">Moteur</Text>}>
              <Text strong>{request.vehicle.engine}</Text>
            </Descriptions.Item>
            <Descriptions.Item label={<Text type="secondary">Puissance</Text>}>
              <Text strong>{request.vehicle.power}</Text>
            </Descriptions.Item>
            <Descriptions.Item label={<Text type="secondary">Boîte</Text>}>
              <Text strong>{request.vehicle.gearbox}</Text>
            </Descriptions.Item>
            <Descriptions.Item label={<Text type="secondary">Année</Text>}>
              <Text strong>{request.vehicle.year}</Text>
            </Descriptions.Item>
            <Descriptions.Item label={<Text type="secondary">Immatriculation</Text>} span={2}>
              <Text code copyable>{request.vehicle.plate}</Text>
            </Descriptions.Item>
            <Descriptions.Item label={<Text type="secondary">VIN</Text>} span={2}>
              <Text code copyable style={{ fontSize: 11 }}>{request.vehicle.vin}</Text>
            </Descriptions.Item>
          </Descriptions>
        </Card>
      </Col>

      {/* ECU Info */}
      <Col span={12}>
        <Card
          title={
            <Space>
              <SettingOutlined style={{ color: token.colorSuccess }} />
              <span>ECU</span>
            </Space>
          }
          size="small"
          styles={{ body: { padding: '16px 20px' } }}
        >
          <Descriptions column={2} size="small" colon={false}>
            <Descriptions.Item label={<Text type="secondary">Marque</Text>}>
              <Text strong>{request.ecu.brand}</Text>
            </Descriptions.Item>
            <Descriptions.Item label={<Text type="secondary">Modèle</Text>}>
              <Text strong>{request.ecu.model}</Text>
            </Descriptions.Item>
            <Descriptions.Item label={<Text type="secondary">Outil de lecture</Text>} span={2}>
              <Text strong>{request.ecu.readingTool}</Text>
            </Descriptions.Item>
            <Descriptions.Item label={<Text type="secondary">N° Hardware</Text>} span={2}>
              <Text code copyable>{request.ecu.hwNumber}</Text>
            </Descriptions.Item>
            <Descriptions.Item label={<Text type="secondary">N° Software</Text>} span={2}>
              <Text code copyable>{request.ecu.swNumber}</Text>
            </Descriptions.Item>
          </Descriptions>
        </Card>
      </Col>

      {/* Tuning Options */}
      <Col span={24}>
        <Card
          title={
            <Space>
              <ThunderboltOutlined style={{ color: token.colorWarning }} />
              <span>Reprogrammation</span>
            </Space>
          }
          size="small"
          extra={
            <Tag color="orange" icon={<CreditCardOutlined />}>
              {request.tuning.credits} crédits
            </Tag>
          }
        >
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                Base
              </Text>
              <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px' }}>
                {request.tuning.base}
              </Tag>
            </Col>
            <Col span={24}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                Options sélectionnées
              </Text>
              <Flex gap={8} wrap="wrap">
                {request.tuning.products.map((product, index) => (
                  <Tag key={index} color="purple">{product}</Tag>
                ))}
              </Flex>
            </Col>
            {request.tuning.comments && (
              <Col span={24}>
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                  Commentaires du client
                </Text>
                <Card
                  size="small"
                  style={{
                    background: hexToRgba(token.colorWarning, 0.1),
                    borderColor: hexToRgba(token.colorWarning, 0.2),
                  }}
                >
                  <Text style={{ color: token.colorWarning }}>{request.tuning.comments}</Text>
                </Card>
              </Col>
            )}
          </Row>
        </Card>
      </Col>
    </Row>
  );

  // Files Tab Content
  const FilesContent = () => (
    <Row gutter={[20, 20]}>
      {/* Received Files */}
      <Col span={12}>
        <Card
          title={
            <Space>
              <Badge status="processing" />
              <span>Fichiers reçus</span>
              <Tag>{request.files.received.length}</Tag>
            </Space>
          }
          size="small"
        >
          <List
            dataSource={request.files.received}
            renderItem={(file) => (
              <List.Item
                actions={[
                  <Tooltip title="Télécharger">
                    <Button type="text" icon={<DownloadOutlined />} />
                  </Tooltip>,
                  <Tooltip title="Voir">
                    <Button type="text" icon={<EyeOutlined />} />
                  </Tooltip>,
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <Avatar
                      shape="square"
                      style={{ background: hexToRgba(token.colorInfo, 0.1) }}
                      icon={<FileTextOutlined style={{ color: token.colorInfo }} />}
                    />
                  }
                  title={<Text ellipsis style={{ maxWidth: 180 }}>{file.name}</Text>}
                  description={
                    <Space size={4}>
                      <Text type="secondary" style={{ fontSize: 11 }}>{file.size}</Text>
                      <Text type="secondary" style={{ fontSize: 11 }}>•</Text>
                      <Text type="secondary" style={{ fontSize: 11 }}>{file.date}</Text>
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        </Card>
      </Col>

      {/* Sent Files */}
      <Col span={12}>
        <Card
          title={
            <Space>
              <Badge status="success" />
              <span>Fichiers envoyés</span>
              <Tag color="green">{request.files.sent.length}</Tag>
            </Space>
          }
          size="small"
        >
          <List
            dataSource={request.files.sent}
            renderItem={(file) => (
              <List.Item
                actions={[
                  <Tooltip title="Télécharger">
                    <Button type="text" icon={<DownloadOutlined />} />
                  </Tooltip>,
                  <Tooltip title="Supprimer">
                    <Button type="text" danger icon={<DeleteOutlined />} />
                  </Tooltip>,
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <Avatar
                      shape="square"
                      style={{ background: hexToRgba(token.colorSuccess, 0.1) }}
                      icon={<FileDoneOutlined style={{ color: token.colorSuccess }} />}
                    />
                  }
                  title={
                    <Flex align="center" gap={8}>
                      <Text ellipsis style={{ maxWidth: 150 }}>{file.name}</Text>
                      <Tag color="success" style={{ fontSize: 10 }}>Livré</Tag>
                    </Flex>
                  }
                  description={
                    <Space size={4}>
                      <Text type="secondary" style={{ fontSize: 11 }}>{file.size}</Text>
                      <Text type="secondary" style={{ fontSize: 11 }}>•</Text>
                      <Text type="secondary" style={{ fontSize: 11 }}>{file.date}</Text>
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        </Card>
      </Col>

      {/* Upload Zone */}
      <Col span={24}>
        <Dragger {...uploadProps} style={{ background: 'rgba(255,255,255,0.02)' }}>
          <p className="ant-upload-drag-icon">
            <CloudUploadOutlined style={{ color: token.colorPrimary, fontSize: 48 }} />
          </p>
          <p className="ant-upload-text">
            Glissez un fichier ici ou cliquez pour sélectionner
          </p>
          <p className="ant-upload-hint" style={{ color: 'rgba(255,255,255,0.45)' }}>
            Formats acceptés: .bin, .ori, .mod (max 50MB)
          </p>
        </Dragger>
      </Col>
    </Row>
  );

  // Tuning Tab Content
  const TuningContent = () => (
    <Row gutter={[20, 20]}>
      <Col span={24}>
        <Card title="Options de reprogrammation" size="small">
          <Flex gap={8} wrap="wrap">
            <Tag color="blue" style={{ padding: '6px 16px', fontSize: 14 }}>
              <ThunderboltOutlined style={{ marginRight: 6 }} />
              {request.tuning.base}
            </Tag>
            {request.tuning.products.map((product, index) => (
              <Tag key={index} color="purple" style={{ padding: '6px 16px', fontSize: 14 }}>
                {product}
              </Tag>
            ))}
          </Flex>
        </Card>
      </Col>

      <Col span={24}>
        <Card title="Commentaires du client" size="small">
          <Card
            size="small"
            style={{
              background: hexToRgba(token.colorWarning, 0.08),
              borderColor: hexToRgba(token.colorWarning, 0.2),
            }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Flex align="center" gap={8}>
                <Avatar size="small" style={{ backgroundColor: token.colorWarning }}>
                  {request.client.avatar}
                </Avatar>
                <Text strong>{request.client.name}</Text>
                <Text type="secondary" style={{ fontSize: 11 }}>• {request.createdAt}</Text>
              </Flex>
              <Text>{request.tuning.comments}</Text>
            </Space>
          </Card>
        </Card>
      </Col>

      <Col span={24}>
        <Card title="Notes internes" size="small">
          <TextArea
            placeholder="Ajouter des notes internes (visibles uniquement par l'équipe)..."
            rows={4}
            style={{ background: 'rgba(255,255,255,0.02)' }}
          />
          <Flex justify="flex-end" style={{ marginTop: 12 }}>
            <Button type="primary" size="small">
              Enregistrer
            </Button>
          </Flex>
        </Card>
      </Col>
    </Row>
  );

  // Tab items
  const tabItems = [
    {
      key: 'details',
      label: (
        <Space>
          <FileTextOutlined />
          <span>Détails</span>
        </Space>
      ),
      children: <DetailsContent />,
    },
    {
      key: 'files',
      label: (
        <Space>
          <FileOutlined />
          <span>Fichiers</span>
          <Badge count={request.files.received.length + request.files.sent.length} size="small" />
        </Space>
      ),
      children: <FilesContent />,
    },
    {
      key: 'tuning',
      label: (
        <Space>
          <ThunderboltOutlined />
          <span>Reprogrammation</span>
        </Space>
      ),
      children: <TuningContent />,
    },
  ];

  return (
      <Layout style={{
        minHeight: '100vh',
        background: `linear-gradient(145deg, ${token.colorBgLayout} 0%, ${token.colorBgContainer} 50%, ${token.colorBgLayout} 100%)`,
      }}>
        {/* Main Content */}
        <Content style={{ padding: '24px 28px', overflowY: 'auto' }}>
          {/* Header */}
          <Flex justify="space-between" align="flex-start" style={{ marginBottom: 24 }}>
            <div>
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                style={{ marginBottom: 8, paddingLeft: 0, color: 'rgba(255,255,255,0.5)' }}
              >
                Retour aux demandes
              </Button>
              <Flex align="center" gap={16}>
                <Title level={2} style={{ margin: 0, color: token.colorTextHeading }}>
                  {request.vehicle.brand} {request.vehicle.model}
                </Title>
                <Tag
                  color={currentStatus?.color}
                  icon={currentStatus?.icon}
                  style={{ fontSize: 13, padding: '4px 12px' }}
                >
                  {currentStatus?.label}
                </Tag>
              </Flex>
              <Text type="secondary" code style={{ marginTop: 8, display: 'inline-block' }}>
                {request.id}
              </Text>
              <Text type="secondary" style={{ marginLeft: 8 }}>
                • Créé le {request.createdAt}
              </Text>
            </div>

            <Space>
              <Button icon={<EditOutlined />}>Modifier</Button>
              <Dropdown
                menu={{
                  items: [
                    { key: '1', label: 'Dupliquer la demande', icon: <CopyOutlined /> },
                    { key: '2', label: 'Exporter', icon: <DownloadOutlined /> },
                    { type: 'divider' },
                    { key: '3', label: 'Supprimer', icon: <DeleteOutlined />, danger: true },
                  ],
                }}
              >
                <Button icon={<MoreOutlined />} />
              </Dropdown>
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                style={{
                  background: `linear-gradient(135deg, ${token.colorSuccess} 0%, ${hexToRgba(token.colorSuccess, 0.85)} 100%)`,
                }}
              >
                Marquer terminée
              </Button>
            </Space>
          </Flex>

          {/* Quick Info Bar */}
          <Card size="small" style={{ marginBottom: 24 }}>
            <Flex justify="space-between" align="center">
              <Space size="large">
                <Flex align="center" gap={12}>
                  <Avatar style={{ backgroundColor: token.colorWarning }}>{request.client.avatar}</Avatar>
                  <div>
                    <Text strong>{request.client.name}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>{request.client.email}</Text>
                  </div>
                </Flex>
                <Divider type="vertical" style={{ height: 40 }} />
                <Statistic
                  title={<Text type="secondary" style={{ fontSize: 11 }}>VIN</Text>}
                  value={request.vehicle.vin}
                  valueStyle={{ fontSize: 13, fontFamily: 'monospace' }}
                />
                <Divider type="vertical" style={{ height: 40 }} />
                <Statistic
                  title={<Text type="secondary" style={{ fontSize: 11 }}>Produit</Text>}
                  value={request.tuning.base}
                  valueStyle={{ fontSize: 14 }}
                  prefix={<ThunderboltOutlined style={{ color: token.colorWarning }} />}
                />
                <Divider type="vertical" style={{ height: 40 }} />
                <Statistic
                  title={<Text type="secondary" style={{ fontSize: 11 }}>Crédits</Text>}
                  value={request.tuning.credits}
                  valueStyle={{ fontSize: 14, color: token.colorWarning }}
                  prefix={<CreditCardOutlined />}
                />
              </Space>
              <Tag color="green">
                {request.client.credits} crédits disponibles
              </Tag>
            </Flex>
          </Card>

          {/* Tabs */}
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
            size="large"
          />
        </Content>

        {/* Right Sidebar */}
        <Sider
          width={380}
          style={{
            background: 'rgba(255,255,255,0.02)',
            borderLeft: '1px solid rgba(255,255,255,0.06)',
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Assignment Section */}
          <div style={{ padding: 20, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
              <Text strong style={{ textTransform: 'uppercase', fontSize: 11, color: 'rgba(255,255,255,0.5)', letterSpacing: 0.5 }}>
                Assignation
              </Text>
              <Button type="link" size="small" onClick={() => setIsAssignModalOpen(true)}>
                Changer
              </Button>
            </Flex>

            {/* Status Selector */}
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                Statut
              </Text>
              <Select
                value={status}
                onChange={setStatus}
                style={{ width: '100%' }}
                options={statusOptions.map(s => ({
                  value: s.value,
                  label: (
                    <Space>
                      {s.icon}
                      <span>{s.label}</span>
                    </Space>
                  ),
                }))}
              />
            </div>

            {/* Assignee */}
            <div>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                Assigné à
              </Text>
              <Card size="small" style={{ background: 'rgba(255,255,255,0.02)' }}>
                <Flex align="center" gap={12}>
                  <Badge dot status={currentAssignee?.available ? 'success' : 'default'} offset={[-4, 32]}>
                    <Avatar
                      size={40}
                      style={{ backgroundColor: currentAssignee?.color }}
                    >
                      {currentAssignee?.avatar}
                    </Avatar>
                  </Badge>
                  <div style={{ flex: 1 }}>
                    <Text strong>{currentAssignee?.name}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>{currentAssignee?.role}</Text>
                  </div>
                  <Flex vertical align="flex-end">
                    <Text type="secondary" style={{ fontSize: 11 }}>Charge</Text>
                    <Progress
                      percent={Math.round(((currentAssignee?.workload ?? 0) / 15) * 100)}
                      size="small"
                      style={{ width: 60, margin: 0 }}
                      strokeColor={
                        (currentAssignee?.workload ?? 0) > 12
                          ? token.colorError
                          : (currentAssignee?.workload ?? 0) > 8
                            ? token.colorWarning
                            : token.colorSuccess
                      }
                    />
                  </Flex>
                </Flex>
              </Card>
            </div>
          </div>

          {/* Chat Section */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <Flex justify="space-between" align="center">
                <Text strong style={{ textTransform: 'uppercase', fontSize: 11, color: 'rgba(255,255,255,0.5)', letterSpacing: 0.5 }}>
                  S.A.V / Chat
                </Text>
                <Badge count={chatMessages.length} size="small" color={token.colorPrimary} />
              </Flex>
            </div>

            {/* Messages */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
              {chatMessages.map((msg) => (
                <div
                  key={msg.id}
                  style={{
                    marginBottom: 16,
                    textAlign: msg.sender === 'system' ? 'center' : msg.sender === 'tuner' ? 'right' : 'left',
                  }}
                >
                  {msg.sender === 'system' ? (
                    <Text type="secondary" style={{ fontSize: 11, fontStyle: 'italic' }}>
                      {msg.message}
                    </Text>
                  ) : (
                    <>
                      <div
                        style={{
                          display: 'inline-block',
                          maxWidth: '85%',
                          padding: '10px 14px',
                          background: msg.sender === 'tuner' ? tunerMessageBg : 'rgba(255,255,255,0.05)',
                          borderRadius: msg.sender === 'tuner'
                            ? '12px 12px 4px 12px'
                            : '12px 12px 12px 4px',
                          border: `1px solid ${msg.sender === 'tuner' ? hexToRgba(token.colorPrimary, 0.2) : 'rgba(255,255,255,0.06)'}`,
                        }}
                      >
                        <Text style={{ fontSize: 13 }}>{msg.message}</Text>
                      </div>
                      <div style={{ marginTop: 4 }}>
                        <Text type="secondary" style={{ fontSize: 10 }}>
                          {msg.name} • {msg.time}
                        </Text>
                      </div>
                    </>
                  )}
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>

            {/* Chat Input */}
            <div style={{ padding: 16, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <Flex gap={8}>
                <TextArea
                  placeholder="Écrire un message..."
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                  autoSize={{ minRows: 1, maxRows: 4 }}
                  style={{ flex: 1, background: 'rgba(255,255,255,0.03)' }}
                  onPressEnter={(e) => {
                    if (!e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                />
                <Flex vertical gap={4}>
                  <Tooltip title="Joindre un fichier">
                    <Button icon={<PaperClipOutlined />} />
                  </Tooltip>
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    disabled={!chatMessage.trim()}
                    onClick={handleSendMessage}
                  />
                </Flex>
              </Flex>
            </div>
          </div>

          {/* Activity Timeline */}
          <div style={{ padding: 20, borderTop: '1px solid rgba(255,255,255,0.06)', maxHeight: 280, overflowY: 'auto' }}>
            <Text strong style={{ textTransform: 'uppercase', fontSize: 11, color: 'rgba(255,255,255,0.5)', letterSpacing: 0.5, display: 'block', marginBottom: 16 }}>
              Historique
            </Text>
            <Timeline
              items={activityLog.map((activity) => ({
                dot: getActivityIcon(activity.type),
                children: (
                  <div>
                    <Text style={{ fontSize: 13 }}>{activity.label}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {activity.date} à {activity.time}
                    </Text>
                  </div>
                ),
              }))}
            />
          </div>
        </Sider>

        {/* Assignment Modal */}
        <Modal
          title="Changer l'assignation"
          open={isAssignModalOpen}
          onCancel={() => setIsAssignModalOpen(false)}
          footer={[
            <Button key="cancel" onClick={() => setIsAssignModalOpen(false)}>
              Annuler
            </Button>,
            <Button key="submit" type="primary" onClick={() => {
              message.success('Assignation mise à jour');
              setIsAssignModalOpen(false);
            }}>
              Confirmer
            </Button>,
          ]}
          width={500}
        >
          <div style={{ marginTop: 16 }}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              Sélectionnez un membre de l'équipe
            </Text>
            <Flex vertical gap={8}>
              {teamMembers.map((member) => (
                <Card
                  key={member.id}
                  hoverable
                  size="small"
                  onClick={() => setSelectedAssignee(member.id)}
                  style={{
                    borderColor: selectedAssignee === member.id ? token.colorPrimary : undefined,
                    background: selectedAssignee === member.id ? selectedTint : undefined,
                  }}
                >
                  <Flex align="center" gap={12}>
                    <Badge dot status={member.available ? 'success' : 'default'} offset={[-4, 32]}>
                      <Avatar style={{ backgroundColor: member.color }}>{member.avatar}</Avatar>
                    </Badge>
                    <div style={{ flex: 1 }}>
                      <Text strong>{member.name}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 12 }}>{member.role}</Text>
                    </div>
                    <Flex vertical align="flex-end" gap={4}>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {member.workload}/15 demandes
                      </Text>
                      <Progress
                        percent={Math.round((member.workload / 15) * 100)}
                        size="small"
                        style={{ width: 80, margin: 0 }}
                        showInfo={false}
                        strokeColor={
                          member.workload > 12
                            ? token.colorError
                            : member.workload > 8
                              ? token.colorWarning
                              : token.colorSuccess
                        }
                      />
                    </Flex>
                    {selectedAssignee === member.id && (
                      <CheckCircleOutlined style={{ color: token.colorPrimary, fontSize: 18 }} />
                    )}
                  </Flex>
                </Card>
              ))}
            </Flex>
          </div>
        </Modal>
      </Layout>
  );
};

export default TunerRequestDetailAntD;